import React from "react"
import Player from "@vimeo/player"
import { CircleButton } from "@shared"
import { css, isMobile } from "@jsUtils"

import { PageSection } from "../PageSection"

import Styles from "./videoVimeo.module.scss"

/**
 * Video section
 * @param {object} props - React props
 */
const VideoVimeo = props => {
  const { color, theme, title, vimeoId, preview } = props
  const [ready, setReady] = React.useState(false)
  const [started, setStarted] = React.useState(false)
  const [playing, setPlaying] = React.useState(false)
  const [duration, setDuration] = React.useState(0)
  const [time, setTime] = React.useState(0)
  const [scrubbing, setScrubbing] = React.useState(false)
  const [layoutMobile, setLayoutMobile] = React.useState(false)
  const iFrame = React.useRef()
  const slider = React.useRef()
  let player = null

  const setVideoTime = newTime => {
    if (player && scrubbing) {
      player.setCurrentTime(newTime).then(actualTime => {
        setTime(actualTime)
      })
    }
  }

  const onTimeUpdate = e => {
    setTime(e.seconds)
  }

  const onFullscreenChange = e => {
    if (!e.fullscreen) {
      setPlaying(false)
    }
  }

  const onResize = () => {
    const currentlyMobile = isMobile()
    if (currentlyMobile && !layoutMobile) {
      setLayoutMobile(true)
      setPlaying(false)
      player.pause()
    } else if (!currentlyMobile && layoutMobile) {
      setPlaying(false)
      player.pause()
      setLayoutMobile(false)
    }
  }

  React.useEffect(() => {
    if (iFrame.current) {
      player = new Player(iFrame.current)
      player.setVolume(1)
      player.getDuration().then(videoDuration => {
        setDuration(videoDuration)
      })
      player.on("timeupdate", onTimeUpdate)
      player.on("fullscreenchange", onFullscreenChange)
      onResize()
      window.addEventListener("resize", onResize)
      return () => {
        player.off("timeupdate")
        player.off("fullscreenchange")
        window.removeEventListener("reize", onResize)
      }
    }
    return () => {}
  })

  React.useEffect(() => {
    if (iFrame.current) {
      if (playing) {
        player.play()
      } else {
        player.pause()
      }
    }
  }, [playing])

  const timeMinutes = Math.floor(time / 60)
  let timeSeconds = Math.round(time % 60)
  timeSeconds = timeSeconds < 10 ? `${timeSeconds}0` : timeSeconds

  return (
    <PageSection
      className={css(Styles.section, ready && Styles.ready)}
      theme={theme}
      ready={ready}
      onReady={() => {
        setReady(true)
      }}
    >
      <div className={Styles.videoWrapper}>
        <iframe
          className={Styles.video}
          title={title}
          ref={iFrame}
          src={`https://player.vimeo.com/video/${vimeoId}?controls=false&autoplay=0&transparent=false&autopause=false&playsinline=false&loop=false`}
          width="640"
          height="360"
          frameBorder="0"
          webkitallowfullscreen
          mozallowfullscreen
          allowFullScreen
          allow="autoplay; encrypted-media"
          data-ready="true"
          style={{ pointerEvents: "none" }}
        />
      </div>
      <div className={css(Styles.controls, started && Styles.visible)}>
        <div className={Styles.bottomControls}>
          <CircleButton
            className={Styles.pausePlayButton}
            color={color}
            content={playing && !layoutMobile ? "pause" : "play"}
            label={playing && !layoutMobile ? "Pause" : "Play"}
            onClick={() => {
              setPlaying(!playing)
              if (playing) {
                player.play()
              } else {
                player.pause()
              }
            }}
          />
          {layoutMobile ? (
            ""
          ) : (
            <>
              <input
                aria-label={`${title} Video Timeline Control`}
                ref={slider}
                className={Styles.slider}
                type="range"
                min="0"
                max={duration}
                value={scrubbing ? null : time}
                onFocus={() => {
                  setScrubbing(true)
                }}
                onBlur={() => {
                  setVideoTime(slider.current.value)
                  setScrubbing(false)
                }}
                onMouseDown={() => {
                  setScrubbing(true)
                }}
                onMouseUp={() => {
                  setVideoTime(slider.current.value)
                  setScrubbing(false)
                }}
                onChange={() => {
                  if (scrubbing) {
                    setVideoTime(slider.current.value)
                  }
                }}
              />
              <div className={Styles.time}>{`${timeMinutes}:${timeSeconds}`}</div>
            </>
          )}
        </div>
      </div>

      <div className={css(Styles.overlay, started && Styles.hidden)}>
        <video className={Styles.preview} autoPlay playsInline muted loop>
          <source src={preview} type="video/mp4" />
        </video>
        <div className={Styles.darken} />
        <CircleButton
          className={Styles.playButton}
          color={color}
          content="play"
          label="Play"
          size="medium"
          onClick={() => {
            setStarted(true)
            setPlaying(true)
            player.play()
          }}
        />
      </div>
    </PageSection>
  )
}

export default VideoVimeo
